import {
  VStack,
  HStack,
  Input,
  Button,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  IconButton,
  Tooltip,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MdRefresh, MdCheck } from 'react-icons/md'

interface SlideIdentifierInputProps {
  onSlideIdConfirmed: (slideId: string) => void
  currentSlideId?: string | null
}

// Generate a simple random ID without external dependencies
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const createSlideIdentifier = () => `SLIDE-${generateUUID().substring(0, 8).toUpperCase()}`

export default function SlideIdentifierInput({
  onSlideIdConfirmed,
  currentSlideId,
}: SlideIdentifierInputProps) {
  const { t } = useTranslation()
  const [slideId, setSlideId] = useState('')
  const [isConfirmed, setIsConfirmed] = useState(false)

  useEffect(() => {
    if (currentSlideId) {
      setSlideId(currentSlideId)
      setIsConfirmed(true)
    } else {
      setSlideId(createSlideIdentifier())
      setIsConfirmed(false)
    }
  }, [currentSlideId])

  const generateRandomId = () => {
    setSlideId(createSlideIdentifier())
    setIsConfirmed(false)
  }

  const handleConfirm = () => {
    if (slideId.trim()) {
      const finalSlideId = slideId.trim()
      setIsConfirmed(true)
      onSlideIdConfirmed(finalSlideId)
    }
  }

  const handleEdit = () => {
    setIsConfirmed(false)
  }

  if (isConfirmed) {
    return (
      <Card bg="purple.50" borderColor="purple.200" borderWidth={1}>
        <CardBody>
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" fontWeight="semibold" color="purple.700">
                {t('lab.parasitology.slideId.currentSlideId')}
              </Text>
              <Text fontSize="md" fontFamily="mono" color="purple.900" fontWeight="bold">
                {slideId}
              </Text>
            </VStack>
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={handleEdit}
              leftIcon={<MdRefresh />}
            >
              {t('lab.parasitology.slideId.change')}
            </Button>
          </HStack>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card bg="purple.50" borderColor="purple.300" borderWidth={2} shadow="sm">
      <CardBody>
        <VStack spacing={4} align="stretch">
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="bold" color="purple.900">
              {t('lab.parasitology.slideId.stepTitle')}
            </Text>
            <Text fontSize="sm" color="purple.700">
              {t('lab.parasitology.slideId.stepDescription')}
            </Text>
          </VStack>

          <FormControl>
            <FormLabel fontSize="sm" color="purple.800" fontWeight="semibold">
              {t('lab.parasitology.slideId.slideIdentifier')}
            </FormLabel>
            <InputGroup>
              <Input
                value={slideId}
                onChange={(e) => setSlideId(e.target.value)}
                placeholder={t('lab.parasitology.slideId.placeholder')}
                bg="white"
                borderColor="purple.300"
                _hover={{ borderColor: 'purple.400' }}
                _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && slideId.trim()) {
                    handleConfirm()
                  }
                }}
              />
              <InputRightElement>
                <Tooltip label={t('lab.parasitology.slideId.generateRandomTooltip')}>
                  <IconButton
                    aria-label={t('lab.parasitology.slideId.generateRandomTooltip')}
                    icon={<MdRefresh />}
                    size="sm"
                    variant="ghost"
                    colorScheme="purple"
                    onClick={generateRandomId}
                  />
                </Tooltip>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <HStack spacing={3}>
            <Button
              colorScheme="purple"
              onClick={handleConfirm}
              isDisabled={!slideId.trim()}
              leftIcon={<MdCheck />}
              flex={1}
            >
              {t('lab.parasitology.slideId.confirmAndContinue')}
            </Button>
            <Button variant="outline" colorScheme="purple" onClick={generateRandomId}>
              {t('lab.parasitology.slideId.generateRandomId')}
            </Button>
          </HStack>

          <Text fontSize="xs" color="purple.600" fontStyle="italic">
            {t('lab.parasitology.slideId.trackingNote')}
          </Text>
        </VStack>
      </CardBody>
    </Card>
  )
}
